import React, { useState } from 'react';
import { Head, Link, useForm, usePage } from '@inertiajs/react';
import { PublicLayout } from '@/components/PublicLayout';

interface Newsletter {
  id: number;
  name: string;
  slug: string;
  description: string | null;
  access_type: 'open_access' | 'restricted_access';
  subscription_type: 'open_subscription' | 'auth_required';
  delivery_schedule: 'manual' | 'daily' | 'weekly' | 'monthly';
  is_active: boolean;
}

interface NewsletterIssue {
  id: number;
  subject: string;
  issue_number: number;
  sent_at: string | null;
  preheader: string | null;
  status: string;
}

interface Props {
  newsletter: Newsletter;
  user?: {
    id: number;
    email: string;
    name: string;
  } | null;
  isSubscribed: boolean;
  sentIssues: NewsletterIssue[] | null;
}

export default function NewsletterShow({ newsletter, user, isSubscribed, sentIssues }: Props) {
  const [subscribing, setSubscribing] = useState(false);
  const { post, delete: destroy, processing } = useForm({});

  const handleSubscribe = () => {
    setSubscribing(true);
    post(`/newsletters/${newsletter.slug}/subscribe_user`, {
      onSuccess: () => {
        setSubscribing(false);
        window.location.reload();
      },
      onError: () => {
        setSubscribing(false);
      }
    });
  };

  const handleUnsubscribe = () => {
    if (confirm('Are you sure you want to unsubscribe from this newsletter?')) {
      destroy(`/newsletters/${newsletter.slug}/unsubscribe`, {
        onSuccess: () => {
          window.location.reload();
        }
      });
    }
  };

  const getScheduleText = (schedule: string) => {
    switch (schedule) {
      case 'daily':
        return 'Sent daily';
      case 'weekly':
        return 'Sent weekly';
      case 'monthly':
        return 'Sent monthly';
      default:
        return 'Sent manually';
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <PublicLayout user={user}>
      <Head title={newsletter.name} />
      <div className="container py-5">
        <div className="row">
          <div className="col-lg-8 mx-auto">
            <Link href="/newsletters" className="text-decoration-none mb-3 d-inline-block">
              ← Back to Newsletters
            </Link>
            
            <h1 className="mb-3">{newsletter.name}</h1>
            
            {newsletter.description && (
              <p className="lead text-muted">{newsletter.description}</p>
            )}
            
            <div className="mb-4">
              <small className="text-muted">
                {getScheduleText(newsletter.delivery_schedule)}
              </small>
            </div>

            {/* Subscription Button */}
            {user ? (
              <div className="mb-5">
                {isSubscribed ? (
                  <div>
                    <span className="badge bg-success me-2">Subscribed</span>
                    <button
                      className="btn btn-outline-danger btn-sm"
                      onClick={handleUnsubscribe}
                      disabled={processing}
                    >
                      {processing ? 'Unsubscribing...' : 'Unsubscribe'}
                    </button>
                  </div>
                ) : (
                  <div>
                    {newsletter.subscription_type === 'auth_required' || newsletter.access_type === 'restricted_access' ? (
                      <button
                        className="btn btn-primary"
                        onClick={handleSubscribe}
                        disabled={subscribing || processing}
                      >
                        {subscribing || processing ? 'Subscribing...' : 'Subscribe'}
                      </button>
                    ) : (
                      <div className="alert alert-info">
                        This newsletter allows email-only subscriptions.{' '}
                        <Link href="/newsletters" className="alert-link">
                          Subscribe with email
                        </Link>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="mb-5">
                <div className="alert alert-info">
                  <Link href="/signin" className="alert-link">
                    Sign in
                  </Link>{' '}
                  to subscribe to this newsletter.
                </div>
              </div>
            )}

            {/* Sent Issues Section */}
            <hr />
            
            <h2 className="h4 mb-4">Past Issues</h2>
            
            {isSubscribed ? (
              sentIssues && sentIssues.length > 0 ? (
                <div className="list-group">
                  {sentIssues.map((issue) => (
                    <Link
                      key={issue.id}
                      href={`/newsletters/${newsletter.slug}/issues/${issue.id}`}
                      className="list-group-item list-group-item-action"
                    >
                      <div className="d-flex w-100 justify-content-between align-items-center">
                        <div>
                          <h5 className="mb-1">
                            {issue.issue_number ? `#${issue.issue_number}: ` : ''}{issue.subject}
                          </h5>
                          {issue.preheader && (
                            <p className="mb-1 text-muted small">{issue.preheader}</p>
                          )}
                        </div>
                        <small className="text-muted">
                          {formatDate(issue.sent_at)}
                        </small>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="alert alert-info">
                  No past issues yet. Check back soon!
                </div>
              )
            ) : (
              <div className="alert alert-secondary">
                <p className="mb-0">
                  <strong>Subscribe to view past issues.</strong> Once subscribed, you'll have access to all sent newsletters.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}
